import React, { useState, useEffect, useMemo, useContext } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline, Polygon, Rectangle, OverlayView } from "@react-google-maps/api";
import { AlertTriangle, Loader2, RefreshCw, RotateCw, Settings2 } from "lucide-react";

const CONFIG_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/google-maps-config`;
export const GLIBS = ["geometry"];
const GoogleMapsContext = React.createContext({
  isLoaded: false,
  loadError: null,
  errorCode: "config_loading",
  mapId: "",
});

async function fetchRuntimeConfig() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(CONFIG_URL, { cache: "no-store", signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return {
      apiKey: String(data?.apiKey || "").trim(),
      mapId: String(data?.mapId || "").trim(),
    };
  } finally {
    clearTimeout(timer);
  }
}

function ConfigStateProvider({ errorCode, loadError, mapId = "", children }) {
  const value = useMemo(() => ({
    isLoaded: false,
    loadError: loadError || new Error(errorCode),
    errorCode,
    mapId,
  }), [errorCode, loadError, mapId]);
  return <GoogleMapsContext.Provider value={value}>{children}</GoogleMapsContext.Provider>;
}

function LoadedMapsProvider({ config, children }) {
  const [authFailure, setAuthFailure] = useState("");
  const mapIds = useMemo(() => (config.mapId ? [config.mapId] : undefined), [config.mapId]);

  // Google 会通过 gm_authFailure 或控制台错误暴露 Key / 账单 / API 启用问题。
  // 两种信号都接住，避免额度异常时页面只剩「加载中」。
  useEffect(() => {
    let active = true;
    const previousAuthFailure = window.gm_authFailure;
    const previousConsoleError = console.error;
    const markAuthFailure = (detail) => {
      if (active) setAuthFailure(detail || "Google Maps authorization failed");
    };
    const onAuthFailure = () => {
      markAuthFailure("Google Maps authorization failed");
      if (typeof previousAuthFailure === "function") previousAuthFailure();
    };
    const onConsoleError = (...args) => {
      const text = args.map((arg) => String(arg?.message || arg || "")).join(" ");
      if (/Google Maps JavaScript API error|MapsDemoKey|InvalidKeyMapError|ExpiredKeyMapError|ApiNotActivatedMapError|BillingNotEnabledMapError|RefererNotAllowedMapError|ApiTargetBlockedMapError|MissingKeyMapError/i.test(text)) {
        markAuthFailure(text);
      }
      previousConsoleError.apply(console, args);
    };
    window.gm_authFailure = onAuthFailure;
    console.error = onConsoleError;
    return () => {
      active = false;
      if (window.gm_authFailure === onAuthFailure) {
        if (typeof previousAuthFailure === "function") window.gm_authFailure = previousAuthFailure;
        else delete window.gm_authFailure;
      }
      if (console.error === onConsoleError) console.error = previousConsoleError;
    };
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "aligcus-gmap",
    googleMapsApiKey: config.apiKey,
    libraries: GLIBS,
    mapIds,
    version: "weekly",
  });

  const loadErrorText = String(loadError?.message || loadError || "");
  const authRelated = /auth|billing|quota|demo.?key|invalid.?key/i.test(loadErrorText);
  const errorCode = authFailure || authRelated ? "auth_error" : loadError ? "load_error" : "";
  const value = useMemo(() => ({
    isLoaded: isLoaded && !errorCode,
    loadError: loadError || (authFailure ? new Error(authFailure) : null),
    errorCode: errorCode || (isLoaded ? "ready" : "config_loading"),
    mapId: config.mapId,
  }), [authFailure, config.mapId, errorCode, isLoaded, loadError]);
  return <GoogleMapsContext.Provider value={value}>{children}</GoogleMapsContext.Provider>;
}

export function GoogleMapsProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    let active = true;
    fetchRuntimeConfig()
      .then((next) => { if (active) setConfig(next); })
      .catch((error) => { if (active) setConfigError(error); });
    return () => { active = false; };
  }, []);

  if (configError) {
    return <ConfigStateProvider errorCode="config_error" loadError={configError}>{children}</ConfigStateProvider>;
  }
  if (!config) {
    return <ConfigStateProvider errorCode="config_loading">{children}</ConfigStateProvider>;
  }
  if (!config.apiKey) {
    return <ConfigStateProvider errorCode="config_missing" mapId={config.mapId}>{children}</ConfigStateProvider>;
  }
  return <LoadedMapsProvider config={config}>{children}</LoadedMapsProvider>;
}

export function useGMaps() {
  return useContext(GoogleMapsContext);
}

const MAP_ERROR_COPY = {
  config_missing: {
    zh: "地图服务尚未配置",
    en: "Google Maps is not configured",
    zhBody: "请管理员进入「系统管理 → 地图配置」填写 Google Maps API Key。",
    enBody: "An administrator must add a Google Maps API key in Admin → Google Maps.",
  },
  auth_error: {
    zh: "Google Maps 授权或额度异常",
    en: "Google Maps authorization or quota error",
    zhBody: "请检查 API Key 限制、Maps JavaScript API 是否启用，以及 Google Cloud 结算额度。",
    enBody: "Check the API key restrictions, Maps JavaScript API enablement, and Google Cloud billing quota.",
  },
  config_error: {
    zh: "地图配置读取失败",
    en: "Could not load map configuration",
    zhBody: "系统暂时无法读取地图配置，请稍后重试。",
    enBody: "The map configuration is temporarily unavailable. Please try again.",
  },
  load_error: {
    zh: "Google Maps 加载失败",
    en: "Google Maps failed to load",
    zhBody: "请检查网络后重试；若持续失败，请管理员检查 API Key 配置。",
    enBody: "Check the network and retry. If it persists, ask an administrator to verify the API key.",
  },
};

export function GoogleMapError({ zh, errorCode = "load_error", className = "", compact = false, testId = "gmap-error" }) {
  const copy = MAP_ERROR_COPY[errorCode] || MAP_ERROR_COPY.load_error;
  const needsConfig = errorCode === "config_missing";
  const Icon = needsConfig ? Settings2 : AlertTriangle;
  return (
    <div role="alert" data-testid={testId} data-error-code={errorCode}
      className={`flex flex-col items-center justify-center gap-2 bg-slate-100 px-4 text-center text-slate-500 ${compact ? "py-3" : "h-full min-h-[160px]"} ${className}`}>
      <Icon className={`${compact ? "h-5 w-5" : "h-7 w-7"} text-amber-600`} />
      <div>
        <p className="text-sm font-semibold text-slate-700">{zh ? copy.zh : copy.en}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{zh ? copy.zhBody : copy.enBody}</p>
      </div>
      {!needsConfig && (
        <button type="button" onClick={() => window.location.reload()}
          className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
          <RefreshCw className="h-3.5 w-3.5" />{zh ? "重新加载" : "Reload"}
        </button>
      )}
    </div>
  );
}

export const ZONE_COLORS = ["#ef4444", "#6366f1", "#16a34a", "#f59e0b", "#0ea5e9"];
const SQM_TO_SQFT = 10.7639;
const FT = 3.28084;

export const toLL = (coords) => (coords || []).map((c) => ({ lat: c[0], lng: c[1] }));
export const asLL = (c) => (Array.isArray(c) ? { lat: c[0], lng: c[1] } : c);
export const boundsFromCorners = (c) => {
  if (!c || c.length !== 2) return null;
  const [a, b] = c;
  return { north: Math.max(a[0], b[0]), south: Math.min(a[0], b[0]), east: Math.max(a[1], b[1]), west: Math.min(a[1], b[1]) };
};
export const cornersCenter = (c) => ({ lat: (c[0][0] + c[1][0]) / 2, lng: (c[0][1] + c[1][1]) / 2 });
export const centroid = (coords) => {
  const n = coords.length || 1;
  return { lat: coords.reduce((s, p) => s + p[0], 0) / n, lng: coords.reduce((s, p) => s + p[1], 0) / n };
};
export function rectDims(a, b) {
  if (!a || !b) return { wft: 0, hft: 0 };
  const latMid = (a[0] + b[0]) / 2;
  const hM = Math.abs(b[0] - a[0]) * 110540;
  const wM = Math.abs(b[1] - a[1]) * 111320 * Math.cos((latMid * Math.PI) / 180);
  return { wft: Math.round(wM * FT), hft: Math.round(hM * FT) };
}
export function polygonAreaSqft(coords) {
  if (!coords || coords.length < 3) return 0;
  const latMid = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const mLat = 110540, mLng = 111320 * Math.cos((latMid * Math.PI) / 180);
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const [y1, x1] = coords[i], [y2, x2] = coords[(i + 1) % coords.length];
    area += (x1 * mLng) * (y2 * mLat) - (x2 * mLng) * (y1 * mLat);
  }
  return Math.round((Math.abs(area) / 2) * SQM_TO_SQFT);
}
export function gZoneStyle(z, i) {
  if (z.kind === "lot") return { strokeColor: "#f59e0b", strokeWeight: 2, strokeOpacity: 0.9, fillOpacity: 0.05, clickable: false };
  if (z.color) return { strokeColor: z.color, strokeWeight: 2.5, strokeOpacity: 0.95, fillColor: z.color, fillOpacity: 0.28, clickable: false };
  if (z.kind === "adu") return { strokeColor: "#ef4444", strokeWeight: 2.5, strokeOpacity: 0.95, fillColor: "#ef4444", fillOpacity: 0.3, clickable: false };
  return { strokeColor: ZONE_COLORS[i % ZONE_COLORS.length], strokeWeight: 2, fillOpacity: 0.25, clickable: false };
}

export function MapLabel({ position, className, children }) {
  return (
    <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}>
      <div className={className} style={{ transform: "translateY(-2px)", whiteSpace: "nowrap", pointerEvents: "none" }}>{children}</div>
    </OverlayView>
  );
}

export function RotationControl({ map, zh, value, onChange, showReset = true }) {
  const [h, setH] = useState(value || 0);
  useEffect(() => { if (value != null) setH(value); }, [value]);
  const apply = (v) => { const n = ((Math.round(v) % 360) + 360) % 360; setH(n); if (map) map.setHeading(n); if (onChange) onChange(n); };
  return (
    <div className="absolute bottom-3 left-3 z-[5] bg-white/95 rounded-lg shadow px-2.5 py-1.5 flex items-center gap-2" data-testid="gmap-rotate">
      <RotateCw className="h-3.5 w-3.5 text-slate-500" />
      <button data-testid="gmap-rotate-minus" onClick={() => apply(h - 15)} className="h-6 w-6 rounded bg-slate-100 hover:bg-slate-200 text-xs">−</button>
      <input data-testid="gmap-rotate-slider" type="range" min="0" max="359" value={h} onChange={(e) => apply(Number(e.target.value))} className="w-28 accent-brand-500" />
      <button data-testid="gmap-rotate-plus" onClick={() => apply(h + 15)} className="h-6 w-6 rounded bg-slate-100 hover:bg-slate-200 text-xs">+</button>
      <span className="text-[11px] text-slate-500 w-9 text-center">{h}°</span>
      {showReset && <button data-testid="gmap-rotate-reset" onClick={() => apply(0)} className="h-6 px-2 rounded bg-slate-800 text-white text-[11px]">{zh ? "正北" : "N"}</button>}
    </div>
  );
}

export function gRenderAnn(a, key) {
  if (!a || !a.coords || !a.coords.length) return null;
  const color = a.color || "#ef4444";
  if (a.type === "marker") {
    const p = asLL(a.coords[0]);
    return (
      <React.Fragment key={key}>
        <Marker position={p} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: color, fillOpacity: 0.95, strokeColor: "#fff", strokeWeight: 2 }} clickable={false} />
        {a.text ? <MapLabel position={p} className="bg-white/90 text-slate-800 text-[11px] rounded px-1.5 py-0.5 shadow -mt-6">{a.text}</MapLabel> : null}
      </React.Fragment>
    );
  }
  if (a.type === "polygon") {
    return (
      <React.Fragment key={key}>
        <Polygon paths={toLL(a.coords)} options={{ strokeColor: color, strokeWeight: 3, fillColor: color, fillOpacity: 0.12, clickable: false }} />
        {a.coords.length >= 3 ? <MapLabel position={centroid(a.coords)} className="bg-emerald-600/85 text-white text-[10px] rounded px-1.5 py-0.5">{polygonAreaSqft(a.coords).toLocaleString()} sf</MapLabel> : null}
      </React.Fragment>
    );
  }
  return <Polyline key={key} path={toLL(a.coords)} options={{ strokeColor: color, strokeWeight: 3, clickable: false }} />;
}

const CONTAINER = { width: "100%", height: "100%" };

// 只读展示地图(业主端/对比)。center 为 [lat,lng]。
export function GLayoutMap({ center, zones, annotations, parcel, zoom = 19, interactive = true, rotate = false, heading = 0, zh }) {
  const { isLoaded, loadError, errorCode, mapId } = useGMaps();
  const [map, setMap] = useState(null);
  if (loadError || (errorCode && errorCode !== "ready" && errorCode !== "config_loading")) {
    return <GoogleMapError zh={zh} errorCode={errorCode} />;
  }
  if (!isLoaded) return <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400 text-sm">Loading map…</div>;
  const c = asLL(center);
  const options = {
    mapId, mapTypeId: "satellite", tilt: 0, heading: heading || 0, disableDefaultUI: true, clickableIcons: false,
    keyboardShortcuts: false, gestureHandling: interactive ? "greedy" : "none", draggable: interactive,
    scrollwheel: interactive, disableDoubleClickZoom: !interactive, maxZoom: 22,
  };
  const onLoad = (m) => { setMap(m); if (heading) m.setHeading(heading); };
  return (
    <div className="relative w-full h-full">
      <GoogleMap mapContainerStyle={CONTAINER} center={c} zoom={zoom} options={options} onLoad={onLoad}>
        {parcel && parcel.coords && (
          <>
            <Polygon paths={toLL(parcel.coords)} options={{ strokeColor: "#ef4444", strokeWeight: 3, strokeOpacity: 0.95, fillColor: "#ef4444", fillOpacity: 0.04, clickable: false }} />
            <MapLabel position={centroid(parcel.coords)} className="bg-red-600/85 text-white text-[10px] rounded px-1.5 py-0.5">{zh ? "真实地块红线" : "Parcel"}{parcel.apn ? ` · ${parcel.apn}` : ""}</MapLabel>
          </>
        )}
        {(zones || []).map((z, i) => {
          const b = boundsFromCorners(z.coords);
          if (!b) return null;
          const d = z.wft ? { wft: z.wft, hft: z.hft } : rectDims(z.coords[0], z.coords[1]);
          return (
            <React.Fragment key={`z-${i}`}>
              <Rectangle bounds={b} options={gZoneStyle(z, i)} />
              <MapLabel position={cornersCenter(z.coords)} className="bg-black/70 text-white text-[11px] rounded px-1.5 py-0.5">{z.label}{d.wft ? ` · ${d.wft}×${d.hft} ft` : ""} · {Number(z.areaSqft).toLocaleString()} sf</MapLabel>
            </React.Fragment>
          );
        })}
        {(annotations || []).map((a, i) => gRenderAnn(a, `a-${i}`))}
      </GoogleMap>
      {rotate && <RotationControl map={map} zh={zh} value={heading} />}
    </div>
  );
}
